import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCompass, FiHome, FiSearch } from "react-icons/fi";
import styles from "../../styles/pages/Product/product-browser.module.css";
import { Product } from "../../interfaces/products/product";
import { getProducts } from "../../data-access/apis/products/productApi";
import { trackAmazonClick } from "../../utils/analytics/trackAmazonClick";
import SEO from "../../utils/seo/SEO";
import { PRODUCT_CATEGORIES, getCategory, getCategoryLabel, getSubCategory } from "../../data/productCategories";

interface ProductBrowserProps {
  categoryOverride?: string;
  searchOverride?: string;
  titleOverride?: string;
  descriptionOverride?: string;
}

const ProductBrowser: React.FC<ProductBrowserProps> = ({ categoryOverride, searchOverride, titleOverride, descriptionOverride }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const categoryFromPath = location.pathname.startsWith("/category/")
    ? decodeURIComponent(location.pathname.split("/")[2] || "")
    : "";
  const routeCategory = params.get("category") || "";
  const category = categoryOverride || categoryFromPath || routeCategory;
  const query = searchOverride || params.get("q") || params.get("search") || "";
  const subCategory = params.get("subCategory") || "";
  const audience = (params.get("audience") || "").toLowerCase() as "boys" | "girls" | "both" | "";
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProducts({ category: category || undefined, subCategory: subCategory || undefined, audience: audience || undefined, search: query || undefined })
      .then((data) => { if (!cancelled) setProducts(data.filter((item) => item.isActive)); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category, subCategory, audience, query]);

  const selectedCategory = getCategory(category);
  const title = titleOverride || (
    subCategory
      ? (getSubCategory(category, subCategory)?.label || subCategory)
      : category
        ? getCategoryLabel(category)
        : query
          ? `Search: ${query}`
          : "Explore Products"
  );
  const sorted = useMemo(() => [...products].sort((a, b) => b.priorityScore - a.priorityScore), [products]);

  return (
    <>
      <SEO title={`${title} | MyOnlineShopping`} description={descriptionOverride || `Explore ${title.toLowerCase()} on MyOnlineShopping and view full product details before visiting Amazon.`} canonicalPath={location.pathname + location.search} noindex={!!query} />
      <main className={styles.page}>
        <div className={styles.container}>
          <div className={styles.top}>
            <button type="button" onClick={() => navigate(-1)} className={styles.back}><FiArrowLeft /> Back</button>
            <span className={styles.count}>{loading ? "Loading…" : `${sorted.length} products`}</span>
          </div>

          <header className={styles.header}>
            <div>
              <p>SHOP THE CATALOGUE</p>
              <h1>{title}</h1>
              <span>{descriptionOverride || "Curated products with marketplace details available when you open the product page."}</span>
            </div>
            <div className={styles.searchBadge}><FiSearch /> {query || "Category browse"}</div>
          </header>

          <div className={styles.chips}>
            <button className={!category && !audience ? styles.activeChip : ""} onClick={() => navigate("/products")}>All</button>
            {PRODUCT_CATEGORIES.map((item) => (
              <button key={item.value} className={category === item.value ? styles.activeChip : ""} onClick={() => navigate(`/category/${item.value}${audience ? `?audience=${audience}` : ""}`)}>{item.label}</button>
            ))}
          </div>

          <div className={styles.chips}>
            <button className={!audience ? styles.activeChip : ""} onClick={() => navigate(category ? `/category/${category}${subCategory ? `?subCategory=${encodeURIComponent(subCategory)}` : ""}` : "/products")}>All audiences</button>
            <button className={audience === "boys" ? styles.activeChip : ""} onClick={() => navigate(category ? `/category/${category}?${subCategory ? `subCategory=${encodeURIComponent(subCategory)}&` : ""}audience=boys` : "/products?audience=boys")}>Boys</button>
            <button className={audience === "girls" ? styles.activeChip : ""} onClick={() => navigate(category ? `/category/${category}?${subCategory ? `subCategory=${encodeURIComponent(subCategory)}&` : ""}audience=girls` : "/products?audience=girls")}>Girls</button>
            <button className={audience === "both" ? styles.activeChip : ""} onClick={() => navigate(category ? `/category/${category}?${subCategory ? `subCategory=${encodeURIComponent(subCategory)}&` : ""}audience=both` : "/products?audience=both")}>Both / Unisex</button>
          </div>

          {selectedCategory && <div className={styles.subCategoryBar}><label>Browse {selectedCategory.label}<select value={subCategory} onChange={e=>{const next=e.target.value;navigate(`/category/${category}?${[next ? `subCategory=${encodeURIComponent(next)}` : "", audience ? `audience=${audience}` : ""].filter(Boolean).join("&")}`)}}><option value="">All sub-categories</option>{selectedCategory.subCategories.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</select></label></div>}

          {loading ? (
            <div className={styles.grid}>{Array.from({ length: 8 }).map((_, i) => <div className={styles.skeleton} key={i} />)}</div>
          ) : sorted.length ? (
            <div className={styles.grid}>
              {sorted.map((product) => (
                <article className={styles.card} key={product._id}>
                  <button className={styles.image} onClick={() => navigate(`/product/${product.slug}`)}>
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.title}
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                          event.currentTarget.parentElement?.classList.add(styles.imageError);
                        }}
                      />
                    ) : <span>No image</span>}
                  </button>
                  <div className={styles.body}>
                    <small>{getCategoryLabel(product.category)}</small>
                    <h2>{product.title}</h2>
                    {product.subCategory && <div className={styles.subCategory}>{getSubCategory(product.category, product.subCategory)?.label || product.subCategory}</div>}
                    <div className={styles.detailHint}>Full Amazon information + community feedback on product page</div>
                    <div className={styles.actions}>
                      <button type="button" onClick={() => navigate(`/product/${product.slug}`)}>
                        View details <span>→</span>
                      </button>
                      <a href={product.amazonUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => trackAmazonClick(product, "product-browser")}>View on Amazon <span>↗</span></a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <section className={styles.empty}>
              <div className={styles.emptyIcon}><FiCompass /></div>
              <span className={styles.emptyKicker}>READY FOR NEW PICKS</span>
              <strong>{query ? "We couldn't find a match yet" : "This shelf is being curated"}</strong>
              <span>
                {query
                  ? "Try a different search, or browse the full catalogue for more products."
                  : "Active products added from the admin panel will appear here automatically."}
              </span>
              <div className={styles.emptyActions}>
                <button type="button" onClick={() => navigate("/products")}><FiCompass /> Browse all products</button>
                <button type="button" className={styles.secondaryEmptyAction} onClick={() => navigate("/")}><FiHome /> Back home</button>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default ProductBrowser;
